import { FormularioDialogService } from '../../../../../auxiliares/servicios/formulario-dialog.service';
import {
  ChangeDetectorRef,
  Component,
  Input,
  OnDestroy,
  OnInit,
  ViewChild,
  signal,
  inject,
  OnChanges,
  SimpleChanges,
  ElementRef,
  HostListener,
} from '@angular/core';
import {
  IQueryParam,
  IFilter,
  IActivo,
  IConfigReenvio,
  IGeoJSONPoint,
  IPopulate,
  IReporteBase,
  IReporteGenerico,
  IReporteTrackerOBD,
  ITrackerStatus,
  IResumenDatosBase,
  IEventoGenerico,
  IUsuario,
  IDashcam,
} from 'modelos/src';
import { Subscription, firstValueFrom } from 'rxjs';
import {
  configReenvioVigente,
  IReenviosTrackerData,
  ReenviosTrackerDialogComponent,
} from '../../../administracion/trackers/reenvios-tracker-dialog/reenvios-tracker-dialog.component';
import { MarkerMapa } from '../../../../../auxiliares/mapa-facil/interfaces';
import * as turf from '@turf/turf';
import { HelperService } from '../../../../../auxiliares/servicios/helper.service';
import {
  etiquetaFuncionActivo,
  etiquetaTipoVehiculo,
} from '../../etiquetas-vehiculo';
// import { OpenLayersService } from '../../../../../auxiliares/servicios/openLayers.service'; // REMOVED: Migrated to Mapbox
import { Lista2Service } from '../../../../../auxiliares/servicios/lista2.service';
import { MapaFacilService } from '../../../../../auxiliares/mapa-facil/mapa-facil.service';
import {
  InfoTableConfig,
  InfoTableData,
  InfoTableHtml,
  htmlInfoTable,
} from '../../../../../componentes/info-table/info-table.component';
import {
  MapCardComponent,
  MapCardConfig,
} from '../../../../../componentes/map-card/map-card.component';
import { ChartCardConfig } from '../../../../../componentes/chart-card/chart-card.component';
import { StatCardConfig } from '../../../../../componentes/stat-card/stat-card.component';
import * as Highcharts from 'highcharts';
import { SeriesOptionsType } from 'highcharts';
import { ChartColors } from '../../../../../auxiliares/servicios/chart-colors.service';
import {
  ITripRouteResponse,
  TriperoService,
} from '../../../../../auxiliares/servicios/http/tripero.service';
import { ResumenesDatosService } from '../../../../../auxiliares/servicios/http/resumenes-datos.service';
import { UsuariosService } from '../../../../../auxiliares/servicios/http/usuarios.service';
import {
  queryRendimientoCombustible,
  vistaRendimientoCombustible,
} from '../../rendimiento-combustible';
import { esColorHex } from '../../../../../auxiliares/html-seguro.util';
import {
  EstadoConexion,
  duracion,
  estadoConexion,
  haceCuanto,
  minutosDesde,
} from '../../../../../auxiliares/estado-conexion.util';
import { TonoEstado } from '../../../../../componentes/detalle/detalle.components';

interface IActivoConReporte extends IActivo {
  ultimoReporte?: IReporteBase<'Tracker 4G'>;
  color?: string;
}

interface TarjetaConfig {
  id: string;
  /** Fila completa de la grilla (gráficos, telemetría…). */
  ancha?: boolean;
}

/** Un dato de una tarjeta (app-dt-dato), con una acción opcional. */
interface FilaDato {
  etiqueta: string;
  valor: string | number | null | undefined;
  mono?: boolean;
  copiable?: boolean;
  accion?: {
    icono: string;
    tooltip: string;
    click: () => void;
    disabled?: boolean;
  };
}

/** Estado de movimiento según Tripero. */
const MOVIMIENTO: Record<string, { tono: TonoEstado; etiqueta: string }> = {
  MOVING: { tono: 'ok', etiqueta: 'En movimiento' },
  IDLE: { tono: 'advertencia', etiqueta: 'Detenido con motor encendido' },
  STOPPED: { tono: 'neutro', etiqueta: 'Estacionado' },
  OFFLINE: { tono: 'apagado', etiqueta: 'Sin conexión' },
  UNKNOWN: { tono: 'neutro', etiqueta: 'Desconocido' },
};

/** Una fila ya formateada de la tarjeta de telemetría OBD. */
interface ItemObd {
  label: string;
  valor: string;
  icon: string;
}

/**
 * Campos conocidos del bloque OBD, en el orden en que se muestran. La clave es
 * la canónica de ReporteTrackerOBDSchema, así que esto sirve para cualquier
 * tracker que lea el bus del vehículo (Queclink, TopFlyTech, el que venga).
 *
 * `unidadDe` apunta al campo que trae la unidad cuando el equipo la informa
 * (nivel de combustible en L o %, consumo en L/h o L/100km).
 */
const CAMPOS_OBD: {
  clave: string;
  label: string;
  icon: string;
  unidad?: string;
  unidadDe?: string;
  decimales?: number;
}[] = [
  { clave: 'rpm', label: 'RPM', icon: 'speed', decimales: 0 },
  {
    clave: 'temperaturaRefrigerante',
    label: 'Temp. refrigerante',
    icon: 'thermostat',
    unidad: '°C',
  },
  {
    clave: 'temperaturaAdmision',
    label: 'Temp. admisión',
    icon: 'thermostat',
    unidad: '°C',
  },
  {
    clave: 'temperaturaAmbiente',
    label: 'Temp. ambiente',
    icon: 'thermostat',
    unidad: '°C',
  },
  {
    clave: 'cargaMotor',
    label: 'Carga del motor',
    icon: 'monitoring',
    unidad: '%',
  },
  {
    clave: 'pedalAcelerador',
    label: 'Acelerador',
    icon: 'trending_up',
    unidad: '%',
  },
  {
    clave: 'presionAdmision',
    label: 'Presión de admisión',
    icon: 'compress',
    unidad: 'kPa',
  },
  { clave: 'flujoAire', label: 'Flujo de aire', icon: 'air', unidad: 'g/s' },
  {
    clave: 'nivelCombustible',
    label: 'Nivel de combustible',
    icon: 'local_gas_station',
    unidadDe: 'nivelCombustibleUnidad',
  },
  {
    clave: 'consumoCombustible',
    label: 'Consumo',
    icon: 'local_gas_station',
    unidadDe: 'consumoCombustibleUnidad',
    decimales: 2,
  },
  {
    clave: 'combustibleUsadoTotal',
    label: 'Combustible consumido',
    icon: 'local_gas_station',
    unidadDe: 'combustibleUsadoTotalUnidad',
    decimales: 2,
  },
  {
    clave: 'velocidad',
    label: 'Velocidad (CAN)',
    icon: 'speed',
    unidad: 'km/h',
  },
  { clave: 'distanciaTotal', label: 'Odómetro del vehículo', icon: 'route' },
  { clave: 'autonomia', label: 'Autonomía', icon: 'map', unidad: 'km' },
  {
    clave: 'horasMotor',
    label: 'Horas de motor',
    icon: 'schedule',
    unidad: 'h',
  },
  {
    clave: 'tiempoConduccion',
    label: 'Horas en marcha',
    icon: 'schedule',
    unidad: 'h',
  },
  {
    clave: 'tiempoRalenti',
    label: 'Horas en ralentí',
    icon: 'schedule',
    unidad: 'h',
  },
  { clave: 'vin', label: 'VIN', icon: 'tag' },
  { clave: 'dtc', label: 'Códigos de falla', icon: 'error_outline' },
];

/**
 * Claves que no son una fila: o son la unidad de otro campo, o son datos de
 * diagnóstico del parser que no le dicen nada al usuario.
 */
const CLAVES_OBD_OCULTAS = new Set([
  'nivelCombustibleUnidad',
  'consumoCombustibleUnidad',
  'combustibleUsadoTotalUnidad',
  'combustibleRalentiUnidad',
  'distanciaTotalFuente',
  'canMaskRaw',
  'rawTail',
  '_parcial',
]);

@Component({
  selector: 'app-detalles-vehiculo-info',
  templateUrl: './detalles-vehiculo-info.component.html',
  styleUrl: './detalles-vehiculo-info.component.scss',
  standalone: false,
})
export class DetallesVehiculoInfoComponent
  implements OnInit, OnChanges, OnDestroy
{
  private formularios = inject(FormularioDialogService);
  helper = inject(HelperService);
  private lista2 = inject(Lista2Service);
  private mapaService = inject(MapaFacilService);
  private triperoService = inject(TriperoService);
  private resumenesDatosService = inject(ResumenesDatosService);
  private usuariosService = inject(UsuariosService);
  private cdr = inject(ChangeDetectorRef);

  @ViewChild(MapCardComponent) private mapCard?: MapCardComponent;

  @Input() vehiculo: IActivoConReporte;

  public mapReady = signal(false);
  public activo: IActivoConReporte;
  public reporte: IReporteBase<'Tracker 4G'>;
  /** El último reporte de cualquier tipo: mide la comunicación. */
  private reporteReciente?: IReporteBase<'Tracker 4G'>;
  public chofer: IUsuario | null = null;
  public showChoferViewer = false;

  private reportes$: Subscription;

  /// Reenvíos que aplican al tracker asignado al vehículo
  private configsReenvio: IConfigReenvio[] = [];
  private puedeVerReenvios = this.helper.tienePermiso(
    'Administración - Ver configuraciones de reenvíos',
  );

  // Loading states granulares
  public loadingVehiculo = true;
  public loadingReporte = signal(true);
  public loadingTrackerStatus = signal(true);
  public loadingMap = signal(true);

  // Configuraciones para info-table
  public infoTableGeneral: InfoTableData;
  public infoTableVehiculo: InfoTableData;
  public infoTableChofer: InfoTableData;
  public infoTableCombustible: InfoTableConfig | null = null;
  public infoTableComunicacion: InfoTableData;
  public infoTableTracker: InfoTableConfig;
  public infoTableRecorrido: InfoTableData;
  public infoTableViajeActual: InfoTableConfig;
  public mapCardConfig: MapCardConfig;
  public chartCardConfigConsumoCombustible: ChartCardConfig;
  public statCardTemperatura: StatCardConfig | null = null;
  public chartCardConfigTemperatura: ChartCardConfig | null = null;
  public statCardNivelCombustible: StatCardConfig | null = null;
  /** Consumo promedio de 30 días medido por el sensor (resumen de gestion-cron). */
  public statCardRendimiento: StatCardConfig | null = null;
  public detalleRendimiento = '';
  public chartCardConfigNivelCombustible: ChartCardConfig | null = null;
  public periodoNivelCombustible: 7 | 30 = 7;
  private pedidoNivelCombustible = 0;

  // Tarjetas de la grilla, en orden fijo (ya no se arrastran ni se
  // redimensionan). El mapa es una tarjeta más: va arriba a la derecha y toma
  // el alto de las dos primeras.
  private readonly TARJETAS: TarjetaConfig[] = [
    { id: 'vehiculo' },
    { id: 'mapa' },
    { id: 'tracker' },
    { id: 'dashcam' },
    { id: 'viaje' },
    { id: 'chofer' },
    { id: 'combustible' },
    { id: 'temperatura' },
    { id: 'recorrido' },
    { id: 'obd', ancha: true },
    { id: 'nivelCombChart', ancha: true },
    { id: 'consumoChart', ancha: true },
    { id: 'informeCombustible', ancha: true },
    { id: 'documentacion', ancha: true },
  ];
  public tarjetas: TarjetaConfig[] = [];

  /** Dashcam asignada (aparte del tracker) y su último reporte de posición. */
  public dashcam?: IDashcam;
  public dashcamCargada = false;
  private fechaReporteDashcam?: string;
  private dashcam$?: Subscription;
  private reporteDashcam$?: Subscription;
  /** Filas de la tarjeta de OBD. Vacío = el vehículo no reporta telemetría. */
  public itemsObd: ItemObd[] = [];
  /** Momento de la lectura que muestra la tarjeta de OBD. */
  public fechaObd?: string;

  // Tracker status (desde Tripero)
  public trackerStatus: ITrackerStatus | null = null;
  // Ruta del trip actual
  public tripRoute: ITripRouteResponse | null = null;
  // Loading state para el gráfico
  public loadingChartCombustible = signal(true);

  // Datos internos para la tarjeta de combustible
  private readonly CONSUMO_DEFAULT_POR_TIPO: Record<string, number> = {
    Auto: 10,
    Camion: 30,
    Camioneta: 12,
    Colectivo: 35,
    Grua: 35,
    Moto: 4,
    Otro: 12,
  };
  private reportesCombustibleData: IReporteBase<'Tracker 4G Combustible'>[] =
    [];
  private eventosCombustibleData: IEventoGenerico[] = [];
  private eventosCombustible$?: Subscription;

  /**
   * Odómetro en km para la tarjeta de historial.
   * Preferimos el del equipo (`valores.odometro`, en metros). Si no lo manda o
   * lo manda en 0 —lo que hace un tracker sin odómetro configurado— se usa el
   * acumulado de Tripero, que ya viene en km.
   */
  public get odometroHistorialKm(): number | null {
    const metros = (this.reporte?.valores as { odometro?: number })?.odometro;
    const kmReporte = metros != null ? metros / 1000 : null;
    if (kmReporte != null && kmReporte > 0) return kmReporte;
    const kmTripero = this.trackerStatus?.odometer?.totalKm;
    return kmTripero != null && kmTripero > 0 ? kmTripero : null;
  }

  /**
   * El historial avisa si tiene algo que mostrar. Si el vehículo no tiene
   * recordatorios ni servicios la columna no se reserva: quedaría una tarjeta
   * con solo el nodo del odómetro, que ya está en "Viaje en Curso".
   */
  public historialConDatos = false;

  public onHistorialDatos(hay: boolean): void {
    this.historialConDatos = hay;
    // El mapa mide su contenedor: al ensanchar/angostar la grilla hay que avisarle
    setTimeout(() => this.mapCard?.getMap()?.resize(), 50);
  }

  /** True cuando el odómetro mostrado no vino del reporte del equipo. */
  public get odometroHistorialCalculado(): boolean {
    const metros = (this.reporte?.valores as { odometro?: number })?.odometro;
    const kmReporte = metros != null ? metros / 1000 : null;
    return (
      !(kmReporte != null && kmReporte > 0) && this.odometroHistorialKm != null
    );
  }

  public listarUltimoReporte(vehiculo: IActivoConReporte): void {
    if (!vehiculo) return;

    this.loadingReporte.set(true);
    const filter: IFilter<IReporteGenerico> = {
      idsAsignados: vehiculo._id,
    };

    // Del más nuevo al más viejo: con un tracker cambiado pueden venir los
    // últimos reportes del tracker anterior (también asignados al vehículo).
    const query: IQueryParam = {
      filter: JSON.stringify(filter),
      sort: '-fechaCreacion',
      includeChildren: true,
    };

    this.reportes$?.unsubscribe();
    this.reportes$ = this.lista2
      .ultimosReportesGenericos<'Tracker 4G'>(query, 30)
      .subscribe((data) => {
        // Hay un último reporte por tipoReporte. Para el mapa, el OBD y el
        // odómetro va el de posición: el más nuevo puede ser uno de combustible
        // o temperatura, sin coordenadas. La comunicación, en cambio, se mide
        // con el más nuevo de cualquier tipo (un reporte de combustible también
        // es el equipo comunicando).
        this.reporteReciente = data.datos[0];
        const reporte =
          data.datos.find((r) => r.tipoReporte === 'Tracker 4G') ??
          data.datos[0];
        this.aplicarReportesSensores(data.datos);
        if (reporte) {
          vehiculo.ultimoReporte = reporte;
          this.reporte = reporte;

          // Calcular color
          const timeReporte = new Date(
            this.reporteReciente.fechaCreacion,
          ).getTime();
          const diff = Date.now() - timeReporte;

          if (vehiculo.tracker?.frecReporte) {
            const frecuenciaMs = vehiculo.tracker.frecReporte * 60 * 1000;

            if (diff > 2 * frecuenciaMs) {
              vehiculo.color = 'red';
            } else if (diff > frecuenciaMs) {
              vehiculo.color = 'yellow';
            } else {
              vehiculo.color = 'green';
            }
          } else {
            vehiculo.color = 'black';
          }

          this.activo = vehiculo;
          this.dataDelmapa();
        } else {
          // No hay reporte, actualizar mapa con mensaje de sin datos
          this.mapCardConfig = {
            title: 'Última ubicación',
            icon: 'location_on',
            color: 'red',
            mapOptions: undefined,
          };
          this.loadingMap.set(false);
        }
        this.prepareInfoTables();
        this.loadingReporte.set(false);
      });
  }

  /**
   * Combustible y temperatura actuales salen de la misma consulta que el
   * último reporte (trae el último de cada tipo y se refresca cada 30 s). Antes
   * había tres consultas aparte, dos de ellas iguales, que no se refrescaban.
   */
  private aplicarReportesSensores(reportes: IReporteGenerico[]): void {
    this.reportesCombustibleData = reportes.filter(
      (r) => r.tipoReporte === 'Tracker 4G Combustible',
    ) as IReporteBase<'Tracker 4G Combustible'>[];
    this.buildInfoTableCombustible();

    // Mismo criterio que la tabla: el último con nivel, sea periódico
    // (nivelCombustibleTotal) o de variación (nivelPostVariacion). Si no, la
    // tarjeta desaparecía cada vez que el último era de variación.
    const conNivel = this.reportesCombustibleData.find(
      (r) =>
        (r.valores as any)?.nivelCombustibleTotal !== undefined ||
        (r.valores as any)?.nivelPostVariacion !== undefined,
    );
    const nivelActual = ((conNivel?.valores as any)?.nivelCombustibleTotal ??
      (conNivel?.valores as any)?.nivelPostVariacion) as number | undefined;
    this.statCardNivelCombustible =
      nivelActual === undefined
        ? null
        : {
            title: 'Nivel de Combustible',
            value: `${nivelActual}`,
            subtitle: 'Sensor de Nivel de Combustible',
            icon: 'local_gas_station',
            color:
              nivelActual < 30 ? 'red' : nivelActual < 60 ? 'yellow' : 'green',
            showShadow: true,
            showBorder: true,
          };

    const temperatura = reportes.find(
      (r) => r.tipoReporte === 'Tracker 4G Temperatura',
    );
    const tempActual = (temperatura?.valores as any)?.temperatura as
      | number
      | undefined;
    this.statCardTemperatura =
      tempActual === undefined
        ? null
        : {
            title: 'Temperatura de Carga',
            value: `${tempActual > 0 ? '+' : ''}${tempActual} °C`,
            subtitle: 'Sensor de Temperatura de Carga',
            icon: 'thermostat',
            color: tempActual > 30 ? 'red' : tempActual < 5 ? 'teal' : 'green',
            showShadow: true,
            showBorder: true,
          };
    this.actualizarTarjetas();
  }

  /**
   * Arma la tarjeta de OBD con lo que haya en el último reporte. Se adapta a lo
   * que mande cada equipo: los campos conocidos salen con etiqueta y unidad en
   * el orden de CAMPOS_OBD, y cualquier clave nueva que agregue un parser se
   * muestra igual, con la clave prettificada, en vez de desaparecer.
   */
  private buildTarjetaObd(): void {
    const obd = this.reporte?.valores?.obd as IReporteTrackerOBD | undefined;
    this.itemsObd = [];
    // La telemetría es una foto de un instante: sin la fecha no se sabe si son
    // datos de hace un minuto o de la última vez que anduvo el vehículo.
    this.fechaObd = this.reporte?.valores?.timestamp;
    if (!obd) return;

    const usadas = new Set<string>(CLAVES_OBD_OCULTAS);

    for (const campo of CAMPOS_OBD) {
      usadas.add(campo.clave);
      const valor = obd[campo.clave];
      if (valor === undefined || valor === null) continue;
      const unidad = campo.unidadDe
        ? ((obd[campo.unidadDe] as string) ?? '')
        : (campo.unidad ?? '');
      this.itemsObd.push({
        label: campo.label,
        icon: campo.icon,
        valor: this.formatearValorObd(valor, unidad, campo.decimales),
      });
    }

    // Lo que el parser mande y esta pantalla todavía no conozca.
    for (const [clave, valor] of Object.entries(obd)) {
      if (usadas.has(clave) || valor === undefined || valor === null) continue;
      this.itemsObd.push({
        label: this.humanizarClave(clave),
        icon: 'help_outline',
        valor: this.formatearValorObd(valor, ''),
      });
    }
  }

  private formatearValorObd(
    valor: unknown,
    unidad: string,
    decimales?: number,
  ): string {
    if (Array.isArray(valor)) {
      return valor.length ? valor.join(', ') : 'Sin información';
    }
    if (typeof valor === 'number') {
      // Por defecto 1 decimal, pero sin arrastrar el ",0" de los enteros.
      const dec = decimales ?? (Number.isInteger(valor) ? 0 : 1);
      const texto = valor.toLocaleString('es-AR', {
        minimumFractionDigits: dec,
        maximumFractionDigits: dec,
      });
      return unidad ? `${texto} ${unidad}` : texto;
    }
    if (typeof valor === 'boolean') return valor ? 'Sí' : 'No';
    const texto = String(valor);
    return unidad ? `${texto} ${unidad}` : texto;
  }

  /** `temperaturaEscape` -> `Temperatura escape`. */
  private humanizarClave(clave: string): string {
    const conEspacios = clave
      .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
      .toLowerCase();
    return conEspacios.charAt(0).toUpperCase() + conEspacios.slice(1);
  }

  private prepareInfoTables(): void {
    this.buildTarjetaObd();
    // Información General
    this.infoTableGeneral = {
      title: 'Información General',
      icon: 'info',
      color: 'blue',
      data: {
        Flota: this.activo?.grupo?.nombre,
        Cliente: this.activo?.cliente?.nombre,
        Función: etiquetaFuncionActivo(this.activo?.funcion) || undefined,
      },
    };

    // Información del Vehículo
    this.infoTableVehiculo = {
      title: 'Información del Vehículo',
      icon: 'local_shipping',
      color: 'purple',
      data: {
        Tipo: etiquetaTipoVehiculo(this.activo?.vehiculo?.tipo) || undefined,
        Patente: this.activo?.vehiculo?.patente,
        'Marca y modelo':
          this.activo?.vehiculo?.marca && this.activo?.vehiculo?.modelo
            ? `${this.activo.vehiculo.marca}-${this.activo.vehiculo.modelo}`
            : null,
        Año: this.activo?.vehiculo?.anio,
        Estado: this.activo?.vehiculo?.estado,
      },
    };

    // Conductor
    this.infoTableChofer = {
      title: 'Conductor',
      icon: 'account_box',
      color: 'teal',
      data: {
        Nombre: this.chofer?.datosPersonales?.nombre || this.chofer?.usuario,
        DNI: this.chofer?.datosPersonales?.dni,
        Teléfono: this.chofer?.datosPersonales?.telefono,
        Email: this.chofer?.datosPersonales?.email,
      },
    };

    // Comunicación
    this.infoTableComunicacion = {
      title: 'Comunicación',
      icon: 'cell_tower',
      color: 'green',
      data: {
        'Última comunicación': this.fechaUltimaComunicacion(),
        'Frecuencia de reporte (minutos)': this.activo?.tracker?.frecReporte
          ? this.activo.tracker.frecReporte.toString()
          : null,
      },
    };

    // Información del Tracker
    const dataTracker: InfoTableData = {
      Nombre: this.activo?.tracker?.nombre,
      Identificación: this.activo?.tracker?.identificacion,
      Tipo: this.activo?.tracker?.tipo,
      'Id Unico': this.activo?.tracker?.uniqueId,
      'Marca y modelo':
        this.activo?.tracker?.modelo?.marca &&
        this.activo?.tracker?.modelo?.modelo
          ? `${this.activo.tracker.modelo.marca}-${this.activo.tracker.modelo.modelo}`
          : null,
    };
    const fieldActionsTracker: InfoTableConfig['fieldActions'] = {};
    // Reenvíos del tracker asignado (solo si hay tracker)
    if (this.puedeVerReenvios && this.activo?.tracker?._id) {
      const vigentes = this.configsReenvio.filter((c) =>
        configReenvioVigente(c),
      );
      dataTracker['Reglas de reenvío'] = vigentes.length
        ? `${vigentes.length} activa${vigentes.length !== 1 ? 's' : ''}`
        : 'No hay activas';
      fieldActionsTracker['Reglas de reenvío'] = {
        icon: 'visibility',
        tooltip: this.configsReenvio.length
          ? 'Ver reglas de reenvío'
          : 'Sin reglas de reenvío',
        disabled: !this.configsReenvio.length,
        click: () => this.verReenvios(),
      };
      const avlVigente = vigentes.some(
        (c) => c.opcionesReenvio?.metodo === 'Objetivo AVL',
      );
      // La emergencia AVL no se declara desde el detalle: solo se avisa si
      // hay una vigente.
      if (avlVigente) {
        dataTracker['Emergencia AVL'] = 'Transmitiendo a Policía de la Ciudad';
      }
    }

    this.infoTableTracker = {
      title: 'Información del Tracker',
      icon: 'router',
      color: 'orange',
      data: dataTracker,
      fieldActions: fieldActionsTracker,
    };

    // Información del Recorrido
    if (this.activo?.vehiculo?.recorrido) {
      // El color lo carga un usuario y va dentro de un style: solo se acepta
      // hexadecimal (XSS almacenado). Un color que no es hex no se muestra.
      const color = this.activo.vehiculo.recorrido.color || '#FF5733';
      const colorIndicator = esColorHex(color)
        ? htmlInfoTable(
            `<span style="display: inline-block; width: 20px; height: 20px; background-color: ${color}; border: 1px solid #000; border-radius: 3px; vertical-align: middle; margin-right: 5px;"></span>${color}`,
          )
        : null;

      this.infoTableRecorrido = {
        title: 'Recorrido',
        icon: 'route',
        color: 'yellow',
        data: {
          Nombre: this.activo.vehiculo.recorrido.nombre,
          Color: colorIndicator,
        },
      };
    }

    this.actualizarTarjetas();
  }

  /// Reenvíos que aplican al tracker asignado: los de la propia entidad, los
  /// del vehículo (siguen al tracker que tenga asignado) y los de "todos los
  /// trackers del cliente" (del cliente dueño, o de un ancestro si reenvía a
  /// hijos).
  private async listarConfigsReenvio(): Promise<void> {
    const tracker = this.activo?.tracker;
    if (!this.puedeVerReenvios || !tracker?._id) return;
    const idCliente = tracker.idCliente ?? this.activo?.idCliente;
    if (!idCliente) return;

    const condiciones: IFilter<IConfigReenvio>[] = [
      { agrupacionReenvio: 'Entidad', idEntidadReenvio: tracker._id },
      ...(this.activo?._id
        ? [{ agrupacionReenvio: 'Entidad', idEntidadReenvio: this.activo._id }]
        : []),
      {
        agrupacionReenvio: 'Todos los trackers del cliente',
        idClienteReenvio: idCliente,
      },
    ];
    const ancestros = tracker.idsAncestros ?? this.activo?.idsAncestros ?? [];
    if (ancestros.length) {
      condiciones.push({
        agrupacionReenvio: 'Todos los trackers del cliente',
        idClienteReenvio: { $in: ancestros },
        reenviarHijos: true,
      });
    }

    const populate: IPopulate[] = [
      { path: 'clienteReenvio', select: 'nombre' },
    ];
    const query: IQueryParam = {
      filter: JSON.stringify({ $or: condiciones }),
      populate: JSON.stringify(populate),
      includeChildren: true,
      sort: '-fechaCreacion',
    };
    try {
      const data = await firstValueFrom(this.lista2.configReenvios(query));
      this.configsReenvio = data.datos;
      this.prepareInfoTables();
    } catch (error) {
      console.error('Error al listar configuraciones de reenvío', error);
    }
  }

  public verReenvios(): void {
    this.formularios.abrir<
      ReenviosTrackerDialogComponent,
      IReenviosTrackerData
    >(ReenviosTrackerDialogComponent, {
      tamano: 'mediano',
      cerrarAfuera: true,
      data: {
        nombreEntidad:
          this.activo?.tracker?.identificacion ??
          this.activo?.tracker?.nombre ??
          undefined,
        configs: this.configsReenvio,
        idTracker: this.activo?.tracker?._id,
      },
    });
  }

  private buildInfoTableCombustible(): void {
    const vehiculo = this.activo?.vehiculo;
    const cap = vehiculo?.capacidadCombustible;

    // Buscar el reporte más reciente con nivel de combustible:
    // primero periódico (nivelCombustibleTotal), luego variación (nivelPostVariacion)
    const ultimoConNivel = this.reportesCombustibleData.find(
      (r) =>
        (r.valores as any)?.nivelCombustibleTotal !== undefined ||
        (r.valores as any)?.nivelPostVariacion !== undefined,
    );
    const nivel: number | undefined =
      (ultimoConNivel?.valores as any)?.nivelCombustibleTotal ??
      (ultimoConNivel?.valores as any)?.nivelPostVariacion;
    const ultimoReporte = this.reportesCombustibleData[0];

    const data: Record<string, string | null> = {};

    // Nivel actual (del sensor)
    if (nivel !== undefined) {
      if (cap) {
        const pct = Math.round((nivel / cap) * 100);
        data['Nivel actual'] = `${nivel} L (${pct}%)`;
      } else {
        data['Nivel actual'] = `${nivel} L`;
      }
    }

    // Capacidad del tanque (dato estático del vehículo)
    if (cap) {
      data['Capacidad del tanque'] = `${cap} L`;
    }

    // Último reporte del sensor
    if (ultimoReporte?.fechaCreacion) {
      data['Último reporte sensor'] = HelperService.parseDate(
        new Date(ultimoReporte.fechaCreacion),
      );
    }

    // Consumo declarado o de referencia
    if (vehiculo?.consumoRuta) {
      data['Consumo en ruta'] = `${vehiculo.consumoRuta} L/100km`;
    }
    if (vehiculo?.consumoCiudad) {
      data['Consumo en ciudad'] = `${vehiculo.consumoCiudad} L/100km`;
    }
    if (!vehiculo?.consumoRuta && !vehiculo?.consumoCiudad) {
      const def = this.CONSUMO_DEFAULT_POR_TIPO[vehiculo?.tipo ?? ''];
      if (def !== undefined) {
        data['Consumo de referencia'] = `${def} L/100km`;
      }
    }

    // Último evento de carga (22C)
    const lastCarga = this.eventosCombustibleData.find(
      (e) => (e.valores as any)?.codigoTracker === '22C',
    );
    if (lastCarga) {
      const fecha = HelperService.parseDate(new Date(lastCarga.fechaCreacion));
      data['Última carga'] =
        `${fecha} · ${(lastCarga.valores as any)?.titulo ?? 'Carga de combustible'}`;
    }

    // Último evento de descarga (22D)
    const lastDescarga = this.eventosCombustibleData.find(
      (e) => (e.valores as any)?.codigoTracker === '22D',
    );
    if (lastDescarga) {
      const fecha = HelperService.parseDate(
        new Date(lastDescarga.fechaCreacion),
      );
      data['Última descarga'] =
        `${fecha} · ${(lastDescarga.valores as any)?.titulo ?? 'Descarga de combustible'}`;
    }

    // Última alarma agua (22W)
    const lastAlarmaAgua = this.eventosCombustibleData.find(
      (e) => (e.valores as any)?.codigoTracker === '22W',
    );
    if (lastAlarmaAgua) {
      const fecha = HelperService.parseDate(
        new Date(lastAlarmaAgua.fechaCreacion),
      );
      data['Alarma agua'] = fecha;
    }

    if (Object.keys(data).length === 0) {
      this.infoTableCombustible = null;
      this.actualizarTarjetas();
      return;
    }

    this.infoTableCombustible = {
      title: 'Combustible',
      icon: 'local_gas_station',
      color: 'green',
      data,
    };
    this.actualizarTarjetas();
  }

  private cargarDatosCombustible(): void {
    if (!this.vehiculo?._id) return;
    const idActivo = this.vehiculo._id;

    // Mostrar datos estáticos inmediatamente
    this.buildInfoTableCombustible();

    // Últimos eventos de combustible (carga 22C, descarga 22D, alarma 22W)
    const queryEventos: IQueryParam = {
      filter: JSON.stringify({
        idEntidad: idActivo,
        'valores.codigoTracker': { $in: ['22C', '22D', '22W'] },
      }),
      sort: '-fechaCreacion',
      limit: 10,
      includeChildren: true,
      excludeTotalCount: true,
    };
    this.eventosCombustible$?.unsubscribe();
    this.eventosCombustible$ = this.lista2
      .eventosGenericos(queryEventos, 0)
      .subscribe((data) => {
        this.eventosCombustibleData = data.datos;
        this.buildInfoTableCombustible();
      });
  }

  public dataDelmapa(): void {
    if (!this.reporte) return;
    // La carga solo hasta que el mapa existe: con loading, map-card destruye
    // el mapa, y el refresco cada 30 s lo rearmaba entero (parpadeo, se perdía
    // el zoom). Después, el config nuevo lo actualiza en el lugar.
    const primeraVez = !this.mapReady();
    if (primeraVez) this.loadingMap.set(true);
    const valores = this.helper.getValores(this.reporte);
    if (!valores?.geojson?.coordinates) {
      this.loadingMap.set(false);
      return;
    }
    const coordinate = (valores.geojson as IGeoJSONPoint).coordinates as [
      number,
      number,
    ];

    const marker: MarkerMapa = {
      label: 'puntoSimple',
      coor: coordinate,
      centrar: true,
    };
    const markers: MarkerMapa[] = [marker];

    const polylines: any[] = [];
    const polygons: any[] = [];

    // Agregar el recorrido de traza si existe
    if (this.activo?.vehiculo?.recorrido?.geojson) {
      const recorrido = this.activo.vehiculo.recorrido;
      const geojson = recorrido.geojson;

      // Las coordenadas ya están en formato lng/lat para Mapbox
      if (geojson.type === 'LineString' && geojson.coordinates) {
        const points = geojson.coordinates as [number, number][];

        polylines.push({
          points,
          color: recorrido.color || '#FF5733',
          width: 4,
          centrar: false,
        });
      }
    }

    // Recorrido dinámico (por puntos): un círculo con el radio real de
    // tolerancia por parada, para ver a qué destinos tiene que ir y con qué
    // margen — mismo estilo que las zonas ya declaradas.
    const recorridoDinamico = this.activo?.vehiculo?.recorridoDinamico;
    if (recorridoDinamico?.paradas?.length) {
      recorridoDinamico.paradas.forEach((parada, i) => {
        if (!parada.geojson?.coordinates) return;
        const circulo = turf.circle(
          parada.geojson.coordinates as [number, number],
          parada.radioMetros ?? 60,
          { steps: 64, units: 'meters' },
        );
        polygons.push({
          points: circulo.geometry.coordinates[0],
          color: recorridoDinamico.color || '#1976d2',
          fillColor: recorridoDinamico.color || '#1976d2',
          width: 2,
          fillOpacity: 0.15,
          label: `${i + 1}`,
        });
      });
    }

    // Agregar la ruta del viaje actual si existe
    if (this.tripRoute?.hasActiveTrip) {
      // Usar vialPath si está disponible, sino snapped
      const coords =
        this.tripRoute.route.vialPath.length > 0
          ? this.tripRoute.route.vialPath
          : this.tripRoute.route.snapped;

      if (coords.length >= 2) {
        // coords ya viene como [number, number][], no necesita conversión
        const points = coords;

        polylines.push({
          points,
          color: '#00BCD4', // Cyan para la ruta del trip actual
          width: 5,
          centrar: false,
        });
      }
    }

    // Generar MapCardConfig
    this.mapCardConfig = {
      // La fecha está en el resumen (Último reporte).
      title: 'Última ubicación',
      subtitle: HelperService.extraerDireccion(
        this.reporte?.valores?.direccion,
      ),
      mapOptions: {
        markers,
        polylines: polylines.length > 0 ? polylines : undefined,
        polygons: polygons.length > 0 ? polygons : undefined,
        // Centro y zoom solo al armarlo: updateData los vuelve a aplicar y el
        // refresco de 30 s le pisaba el zoom y el encuadre al usuario.
        center: primeraVez ? coordinate : undefined,
        zoom: primeraVez ? 15 : undefined,
      },
      color: 'red',
    };
    this.mapReady.set(true);
    this.loadingMap.set(false);
    this.cdr.detectChanges();
  }

  public fechaUltimaComunicacion(): InfoTableHtml | null {
    if (!this.reporteReciente) return null;

    const fecha = new Date(this.reporteReciente.fechaCreacion);
    const color = this.calcularColor();

    const colorIconoMap: Record<string, string> = {
      green:
        '<span class="material-icons" style="font-size: 12px; color: #4caf50; vertical-align: middle;">circle</span>',
      yellow:
        '<span class="material-icons" style="font-size: 12px; color: #fbc02d; vertical-align: middle;">circle</span>',
      red: '<span class="material-icons" style="font-size: 12px; color: #f44336; vertical-align: middle;">circle</span>',
      black:
        '<span class="material-icons" style="font-size: 12px; color: #616161; vertical-align: middle;">circle</span>',
    };

    const icono =
      colorIconoMap[color] ||
      '<span class="material-icons" style="font-size: 12px; color: #616161; vertical-align: middle;">circle</span>';

    const fechaFormateada = HelperService.parseDate(fecha);

    return htmlInfoTable(`${icono} ${fechaFormateada}`);
  }

  private calcularColor(): string {
    if (!this.reporteReciente) return 'black';

    const fechaUltimaComunicacion = new Date(
      this.reporteReciente.fechaCreacion,
    );
    if (this.activo?.tracker?.frecReporte) {
      const frecuenciaReporte = this.activo.tracker.frecReporte;
      const diff = Date.now() - fechaUltimaComunicacion.getTime();
      const frecuenciaEnMs = frecuenciaReporte * 60 * 1000;

      if (diff > 2 * frecuenciaEnMs) return 'red';
      if (diff > frecuenciaEnMs) return 'yellow';
      return 'green';
    }
    return 'black';
  }

  /**
   * Carga el estado del tracker desde Tripero
   */
  private async loadTrackerStatus(): Promise<void> {
    if (!this.vehiculo?._id) return;

    this.loadingTrackerStatus.set(true);
    try {
      // El status sale de Tripero (Redis) y trae TODO lo que muestra la tarjeta
      // "Viaje en Curso". Se espera solo esto.
      const statusResponse = await this.triperoService.getTrackerStatus(
        this.vehiculo._id,
      );

      if (statusResponse?.success && statusResponse.data) {
        this.trackerStatus = statusResponse.data;
        await this.prepareViajeActual();
      } else {
        this.infoTableViajeActual = {
          title: 'Viaje en Curso',
          icon: 'directions_car',
          color: 'green',
          data: {},
          emptyMessage: 'Sin datos de Tripero',
        };
      }
    } catch (error) {
      console.error('Error al obtener estado del tracker:', error);
    } finally {
      this.loadingTrackerStatus.set(false);
    }

    // La ruta del trip alimenta SOLO la polilínea del mapa y consulta los
    // reportes del viaje, así que es órdenes de magnitud más lenta. Va fuera
    // del await: estaba dentro de un Promise.all con el status y bloqueaba la
    // tarjeta durante decenas de segundos para pintar datos que ya tenía.
    this.triperoService
      .getTripRoute(this.vehiculo._id)
      .then((tripRouteResponse) => {
        if (tripRouteResponse?.hasActiveTrip) {
          this.tripRoute = tripRouteResponse;
          // Actualizar el mapa con la ruta del trip
          this.dataDelmapa();
        }
      })
      .catch((error) => {
        console.error('Error al obtener la ruta del viaje actual:', error);
      });
  }

  /**
   * Obtiene y procesa datos para el gráfico de consumo de combustible usando IResumenDatos
   */
  private async generarChartConsumoCombustible(): Promise<void> {
    this.loadingChartCombustible.set(true);
    try {
      const filterResumen: IFilter<
        IResumenDatosBase<'Consumo Mensual Combustible Vehículos'>
      > = {
        tipo: 'Consumo Mensual Combustible Vehículos',
        agrupacion: 'Individual',
        agrupacionTiempo: 'Mensual',
        idCliente: this.vehiculo?.idCliente,
        idsAsignados: this.vehiculo?._id,
      };

      const queryResumen: IQueryParam = {
        filter: JSON.stringify(filterResumen),
        sort: '-periodoInicio',
        limit: 3,
        includeChildren: true,
      };

      const resumen: IResumenDatosBase<'Consumo Mensual Combustible Vehículos'>[] =
        (
          await this.resumenesDatosService.getFiltered<'Consumo Mensual Combustible Vehículos'>(
            queryResumen,
          )
        ).datos;

      // "Últimos 3 meses": los 3 meses calendario hasta el actual. Un mes sin
      // resumen va en 0 (antes se tomaban los 3 últimos documentos, que podían
      // ser meses viejos o salteados).
      const claveMes = (iso: string | Date) => {
        // +12 h: el período empieza a la medianoche local, que en UTC puede
        // caer el día anterior.
        const d = new Date(new Date(iso).getTime() + 12 * 3600 * 1000);
        return d.getUTCFullYear() * 12 + d.getUTCMonth();
      };
      const porMes = new Map(
        resumen.map((r) => [claveMes(r.periodoInicio!), r] as const),
      );
      const hoy = new Date();
      const ultimos = [2, 1, 0].map((atras) => {
        const inicio = new Date(
          Date.UTC(hoy.getFullYear(), hoy.getMonth() - atras, 1, 12),
        );
        return (
          porMes.get(claveMes(inicio)) ??
          ({
            periodoInicio: inicio.toISOString(),
            resumen: {
              kmRecorridos: 0,
              consumoEstimado: 0,
              consumoDeclarado: 0,
            },
          } as IResumenDatosBase<'Consumo Mensual Combustible Vehículos'>)
        );
      });

      // Sin km en esos meses → ocultar el gráfico
      if (!ultimos.some((r) => (r.resumen?.kmRecorridos ?? 0) > 0)) {
        this.chartCardConfigConsumoCombustible = null;
        this.actualizarTarjetas();
        this.loadingChartCombustible.set(false);
        return;
      }

      const categorias = ultimos.map((d) => {
        const fecha = new Date(d.periodoInicio!);
        const monthNames = [
          'Ene',
          'Feb',
          'Mar',
          'Abr',
          'May',
          'Jun',
          'Jul',
          'Ago',
          'Sep',
          'Oct',
          'Nov',
          'Dic',
        ];
        // Misma corrección que claveMes: el período empieza a la medianoche
        // local, que en UTC puede caer el día (y el mes) anterior.
        const corrida = new Date(fecha.getTime() + 12 * 3600 * 1000);
        return `${monthNames[corrida.getUTCMonth()]}/${corrida.getUTCFullYear()}`;
      });

      const datosKm = ultimos.map((d) => {
        return Math.round(d.resumen?.kmRecorridos || 0);
      });

      const datosConsumo = ultimos.map((d) => {
        return Math.round((d.resumen?.consumoEstimado || 0) * 10) / 10;
      });

      const datosConsumoDeclarado = ultimos.map((d) => {
        return Math.round((d.resumen?.consumoDeclarado || 0) * 10) / 10;
      });

      const datosConsumoReal = ultimos.map((d) => {
        return Math.round((d.resumen?.consumoReal || 0) * 10) / 10;
      });

      const series: SeriesOptionsType[] = [
        {
          type: 'column',
          name: 'Km Recorridos',
          data: datosKm,
          yAxis: 0,
          color: ChartColors.color1(),
          tooltip: {
            valueSuffix: ' km',
          },
        },
        {
          type: 'column',
          name: 'Consumo Estimado',
          data: datosConsumo,
          yAxis: 1,
          color: ChartColors.color2(),
          tooltip: {
            valueSuffix: ' L',
          },
        },
        {
          type: 'column',
          name: 'Consumo Declarado',
          data: datosConsumoDeclarado,
          yAxis: 1,
          color: ChartColors.color3(),
          tooltip: {
            valueSuffix: ' L',
          },
        },
        {
          type: 'column',
          name: 'Consumo Real',
          data: datosConsumoReal,
          yAxis: 1,
          color: ChartColors.color4(),
          tooltip: {
            valueSuffix: ' L',
          },
        },
      ];

      const chartOptions: Highcharts.Options = {
        chart: {
          type: 'column',
        },
        title: {
          text: undefined,
        },
        xAxis: {
          categories: categorias,
          title: {
            text: 'Mes/Año',
          },
        },
        yAxis: [
          {
            title: {
              text: 'Kilómetros Recorridos',
            },
            labels: {
              format: '{value} km',
            },
          },
          {
            title: {
              text: 'Consumo Estimado (L)',
            },
            labels: {
              format: '{value} L',
            },
            opposite: true,
          },
        ],
        legend: {
          enabled: true,
        },
        plotOptions: {
          column: {
            grouping: true,
            pointPadding: 0.1,
            borderWidth: 0,
          },
        },
        series,
      };

      this.chartCardConfigConsumoCombustible = {
        title: 'Consumo de Combustible (Últimos 3 meses)',
        icon: 'local_gas_station',
        color: 'blue',
        chartOptions,
      };
      this.actualizarTarjetas();
    } catch (error) {
      console.error(
        'Error al generar gráfico de consumo de combustible:',
        error,
      );
      this.chartCardConfigConsumoCombustible = null;
      this.actualizarTarjetas();
    } finally {
      this.loadingChartCombustible.set(false);
    }
  }

  /**
   * Prepara la info table del viaje actual
   */
  private async prepareViajeActual(): Promise<void> {
    // Calcular estado actual del vehículo con ícono
    const stateIconos: Record<string, string> = {
      MOVING:
        '<span class="material-icons" style="font-size: 14px; vertical-align: middle;">directions_car</span> En movimiento',
      IDLE: '<span class="material-icons" style="font-size: 14px; vertical-align: middle;">pause_circle</span> Detenido (motor encendido)',
      STOPPED:
        '<span class="material-icons" style="font-size: 14px; vertical-align: middle;">local_parking</span> Estacionado',
      UNKNOWN:
        '<span class="material-icons" style="font-size: 14px; vertical-align: middle;">help</span> Desconocido',
      OFFLINE:
        '<span class="material-icons" style="font-size: 14px; vertical-align: middle;">mobile_off</span> Sin conexión',
    };
    const estadoActual = this.trackerStatus?.currentState?.state
      ? htmlInfoTable(
          stateIconos[this.trackerStatus.currentState.state] ||
            '<span class="material-icons" style="font-size: 14px; vertical-align: middle;">help</span> Desconocido',
        )
      : null;

    // Odómetro total acumulado
    const odometroKm = this.trackerStatus?.odometer?.totalKm;
    const odometroStr =
      odometroKm != null ? `${odometroKm.toLocaleString('es-AR')} km` : null;

    if (!this.trackerStatus?.currentTrip) {
      // Sin viaje activo: mostrar estado y odómetro si están disponibles
      this.infoTableViajeActual = {
        title: 'Viaje en Curso',
        icon: 'directions_car',
        color: 'green',
        data: {
          Estado: estadoActual,
          Odómetro: odometroStr,
        },
        emptyMessage: 'No hay viaje en curso',
      };
      return;
    }

    const trip = this.trackerStatus.currentTrip;
    const startTime = new Date(trip.startTime);

    // Formatear hora de inicio
    const horaInicio = HelperService.parseDate(startTime);

    // Construir data con coordenadas como placeholder mientras llega el geocode
    const coordsStr =
      trip.startLat && trip.startLon
        ? `${trip.startLat.toFixed(5)}, ${trip.startLon.toFixed(5)}`
        : null;

    const data: InfoTableData = {
      Inicio: horaInicio,
      Duración: this.helper.parseSeconds(trip.duration),
      Distancia: `${this.helper.mToKm(trip.distance, true)} km`,
      Odómetro: odometroStr,
      'Vel. Promedio': `${Math.round(trip.avgSpeed)} km/h`,
      'Vel. Máxima': `${Math.round(trip.maxSpeed)} km/h`,
      Estado: estadoActual,
      Desde: coordsStr,
    };

    // Mostrar tabla inmediatamente con coordenadas
    this.infoTableViajeActual = {
      title: 'Viaje en Curso',
      icon: 'directions_car',
      color: 'green',
      data: { ...data },
    };

    // Geocoding en background: actualiza "Desde" sin bloquear el render
    if (trip.startLat && trip.startLon) {
      this.triperoService
        .geocode(trip.startLat, trip.startLon)
        .then((result) => {
          if (result?.address) {
            this.infoTableViajeActual = {
              ...this.infoTableViajeActual,
              data: { ...data, Desde: result.address },
            };
          }
        })
        .catch(() => {
          // coordsStr ya está en la tabla, no hace falta hacer nada
        });
    }
  }

  private async cargarChofer(): Promise<void> {
    const idChofer = this.activo?.vehiculo?.idChofer;
    if (!idChofer) return;

    try {
      // idChofer es el _id del Usuario (FK de identidad). Traer por GET
      // /usuarios/:id; getConductoresFiltered ahora lista Permisos y filtraría
      // por _id del permiso, no del usuario.
      this.chofer = (await this.usuariosService.getById(idChofer)) ?? null;
      this.prepareInfoTables();
    } catch {
      // si falla, la tarjeta simplemente no aparece
    }
  }

  private cargarDatosTemperatura(): void {
    if (!this.vehiculo?._id) return;

    // Gráfico: resumenes horarios (últimas 168 horas = 1 semana)
    this.resumenesDatosService
      .getFiltered<'Temperatura Horaria Vehículos'>({
        filter: JSON.stringify({
          idsAsignados: this.vehiculo._id,
          tipo: 'Temperatura Horaria Vehículos',
        }),
        // Las últimas 168 horas: se piden de la más nueva a la más vieja y se
        // dan vuelta para el gráfico (con orden ascendente traía las primeras).
        sort: '-periodoInicio',
        limit: 168,
        includeChildren: true,
      })
      .then((resultado) => {
        const resumenes = [...resultado.datos].reverse();
        if (resumenes.length === 0) {
          this.chartCardConfigTemperatura = null;
          this.actualizarTarjetas();
          return;
        }

        // Datos como [timestamp, valor] para eje datetime + navigator

        const avg: [number, number][] = resumenes.map((r) => [
          new Date(r.periodoInicio).getTime(),
          (r.resumen as any)?.temperaturaPromedio ?? null,
        ]);

        const min: [number, number][] = resumenes.map((r) => [
          new Date(r.periodoInicio).getTime(),
          (r.resumen as any)?.temperaturaMin ?? null,
        ]);

        const max: [number, number][] = resumenes.map((r) => [
          new Date(r.periodoInicio).getTime(),
          (r.resumen as any)?.temperaturaMax ?? null,
        ]);

        this.chartCardConfigTemperatura = {
          title: 'Historial de Temperatura de Carga',
          icon: 'thermostat',
          color: 'teal',
          showShadow: true,
          chartHeight: '420px',
          constructorType: 'stockChart',
          chartOptions: {
            chart: { type: 'spline' },
            rangeSelector: { enabled: false },
            title: { text: undefined },
            xAxis: {
              type: 'datetime',
              dateTimeLabelFormats: {
                hour: '%e %b %H:%M',
                day: '%e %b',
              },
            },
            yAxis: {
              title: { text: 'Temperatura (°C)' },
              labels: { format: '{value} °C' },
            },
            tooltip: {
              valueSuffix: ' °C',
              shared: true,
              xDateFormat: '%e %b %Y %H:%M',
            },
            navigator: { enabled: true },
            series: [
              {
                type: 'spline',
                name: 'Promedio',
                data: avg,
                color: ChartColors.color1(),
              },
              {
                type: 'spline',
                name: 'Mínima',
                data: min,
                color: ChartColors.color2(),
                dashStyle: 'Dash',
              },
              {
                type: 'spline',
                name: 'Máxima',
                data: max,
                color: ChartColors.color3(),
                dashStyle: 'Dash',
              },
            ] as SeriesOptionsType[],
          },
        };
        this.actualizarTarjetas();
      })
      .catch((error) => {
        console.error('Error al cargar el historial de temperatura', error);
        this.chartCardConfigTemperatura = null;
        this.actualizarTarjetas();
      });
  }

  private cargarDatosNivelCombustible(): void {
    if (!this.vehiculo?._id) return;

    // Gráfico: últimos N días de datos disponibles (no anclado a "hoy",
    // porque el vehículo puede haber dejado de reportar hace tiempo). Si se
    // cambia de período antes de que llegue la respuesta, la vieja se descarta.
    const pedido = ++this.pedidoNivelCombustible;
    this.resumenesDatosService
      .getFiltered({
        filter: JSON.stringify({
          idsAsignados: this.vehiculo._id,
          tipo: 'Combustible Horario Vehículos',
        }),
        sort: '-periodoInicio',
        limit: this.periodoNivelCombustible * 24,
        includeChildren: true,
      })
      .then((resultado) => {
        if (pedido !== this.pedidoNivelCombustible) return;
        const resumenes = [...resultado.datos].reverse();
        if (resumenes.length === 0) {
          this.chartCardConfigNivelCombustible = null;
          this.actualizarTarjetas();
          return;
        }

        const avg: [number, number][] = resumenes.map((r) => [
          new Date(r.periodoInicio).getTime(),
          (r.resumen as any)?.nivelPromedio ?? null,
        ]);

        const min: [number, number][] = resumenes.map((r) => [
          new Date(r.periodoInicio).getTime(),
          (r.resumen as any)?.nivelMin ?? null,
        ]);

        const max: [number, number][] = resumenes.map((r) => [
          new Date(r.periodoInicio).getTime(),
          (r.resumen as any)?.nivelMax ?? null,
        ]);

        this.chartCardConfigNivelCombustible = {
          title: 'Historial de Nivel de Combustible',
          icon: 'local_gas_station',
          color: 'orange',
          showShadow: true,
          chartHeight: '420px',
          constructorType: 'stockChart',
          chartOptions: {
            chart: { type: 'spline' },
            rangeSelector: { enabled: false },
            title: { text: undefined },
            xAxis: {
              type: 'datetime',
              dateTimeLabelFormats: {
                hour: '%e %b %H:%M',
                day: '%e %b',
              },
            },
            yAxis: {
              title: { text: 'Nivel' },
            },
            tooltip: {
              shared: true,
              xDateFormat: '%e %b %Y %H:%M',
            },
            navigator: { enabled: true },
            series: [
              {
                type: 'spline',
                name: 'Promedio',
                data: avg,
                color: ChartColors.color1(),
              },
              {
                type: 'spline',
                name: 'Mínimo',
                data: min,
                color: ChartColors.color2(),
                dashStyle: 'Dash',
              },
              {
                type: 'spline',
                name: 'Máximo',
                data: max,
                color: ChartColors.color3(),
                dashStyle: 'Dash',
              },
            ] as SeriesOptionsType[],
          },
        };
        this.actualizarTarjetas();
      })
      .catch((error) => {
        if (pedido !== this.pedidoNivelCombustible) return;
        console.error(
          'Error al cargar el historial de nivel de combustible',
          error,
        );
        this.chartCardConfigNivelCombustible = null;
        this.actualizarTarjetas();
      });
  }

  private async cargarRendimientoCombustible(): Promise<void> {
    const idActivo = this.vehiculo?._id;
    if (!idActivo) return;
    try {
      const res =
        await this.resumenesDatosService.getFiltered<'Rendimiento Combustible Vehículos'>(
          queryRendimientoCombustible(idActivo),
        );
      const vista = vistaRendimientoCombustible(
        res.datos[0]?.resumen,
        this.vehiculo?.vehiculo?.modoConsumo,
      );
      if (!vista) {
        this.statCardRendimiento = null;
      } else {
        this.detalleRendimiento = vista.detalle;
        this.statCardRendimiento = {
          title: 'Consumo promedio',
          value: vista.principal ?? 'Sin datos',
          subtitle: vista.principal
            ? [vista.secundario, 'últimos 30 días'].filter(Boolean).join(' · ')
            : vista.sinDatos,
          icon: 'speed',
          color: 'blue',
          showShadow: true,
          showBorder: true,
        };
      }
    } catch {
      this.statCardRendimiento = null;
    }
    this.actualizarTarjetas();
  }

  public togglePeriodoNivelCombustible(p: 7 | 30): void {
    this.periodoNivelCombustible = p;
    this.cargarDatosNivelCombustible();
  }

  //// Hooks

  async ngOnInit(): Promise<void> {
    this.loadingVehiculo = true;
    if (this.vehiculo) {
      this.activo = this.vehiculo;

      // Inicializar mapCardConfig
      this.mapCardConfig = {
        title: 'Última ubicación',
        icon: 'location_on',
        color: 'red',
      };

      // Inicializar infoTableViajeActual
      this.infoTableViajeActual = {
        title: 'Viaje en Curso',
        icon: 'directions_car',
        color: 'green',
        data: {},
      };

      this.loadingVehiculo = false;

      // Construir tablas con los datos ya disponibles del Input (sin esperar el reporte)
      this.prepareInfoTables();

      // Cargar reporte, tracker status, gráfico y datos de combustible en paralelo
      this.listarUltimoReporte(this.activo);
      this.loadTrackerStatus();
      this.generarChartConsumoCombustible();
      this.cargarDatosCombustible();
      this.cargarDatosTemperatura();
      this.cargarDatosNivelCombustible();
      this.cargarRendimientoCombustible();
      this.cargarChofer();
      this.listarConfigsReenvio();
      this.cargarDashcam();
    } else {
      this.loadingVehiculo = false;
    }
  }

  /**
   * Dashcam asignada: se pide aparte, con su modelo y su SIM, y su último
   * reporte de posición ('Dashcam 4G') para el estado de conexión. No depende
   * de que el backend la popule en el vehículo.
   */
  private cargarDashcam(): void {
    const id = this.activo?.idDashcam;
    this.dashcam$?.unsubscribe();
    this.reporteDashcam$?.unsubscribe();
    if (!id) {
      this.dashcam = undefined;
      this.fechaReporteDashcam = undefined;
      return;
    }
    // Si el backend ya la populó, se muestra mientras llega la consulta
    this.dashcam ??= this.activo?.dashcam;
    const populate: IPopulate[] = [
      { path: 'modelo', select: 'marca modelo' },
      { path: 'sim1' },
    ];
    const query: IQueryParam = {
      filter: JSON.stringify({ _id: id }),
      populate: JSON.stringify(populate),
      includeChildren: true,
      limit: 1,
    };
    this.dashcam$ = this.lista2.dashcams(query).subscribe({
      next: (data) => {
        this.dashcam = data.datos?.[0] ?? this.activo?.dashcam;
        this.dashcamCargada = true;
      },
      error: (error) => {
        this.dashcamCargada = true;
        console.error('Error al cargar la dashcam', error);
      },
    });
    const filter: IFilter<IReporteGenerico> = {
      idEntidad: id,
      tipoReporte: 'Dashcam 4G',
    } as IFilter<IReporteGenerico>;
    this.reporteDashcam$ = this.lista2
      .ultimosReportesGenericos(
        {
          filter: JSON.stringify(filter),
          sort: '-fechaCreacion',
          includeChildren: true,
        },
        30,
      )
      .subscribe({
        next: (data) =>
          (this.fechaReporteDashcam = data.datos?.[0]?.fechaCreacion),
        error: (error) =>
          console.error('Error al cargar el reporte de la dashcam', error),
      });
  }

  /**
   * La página vuelve a pedir el vehículo (por ejemplo al finalizar un
   * recorrido dinámico): se rearma lo que depende de sus datos. Antes Info lo
   * leía solo en ngOnInit y seguía mostrando el objeto viejo (y el refresco de
   * 30 s lo seguía usando).
   */
  ngOnChanges(changes: SimpleChanges): void {
    const cambio = changes['vehiculo'];
    if (!cambio || cambio.firstChange || !this.vehiculo) return;
    this.activo = this.vehiculo;
    this.prepareInfoTables();
    this.listarUltimoReporte(this.activo);
    this.loadTrackerStatus();
    this.cargarChofer();
    this.listarConfigsReenvio();
    this.cargarDashcam();
  }

  ngOnDestroy(): void {
    this.observadorMapa?.disconnect();
    this.reportes$?.unsubscribe();
    this.dashcam$?.unsubscribe();
    this.reporteDashcam$?.unsubscribe();
    this.eventosCombustible$?.unsubscribe();
    this.mapaService.clearMap();
  }

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  // VISTA CON LAS PIEZAS DEL DETALLE (skill detalle-entidad, en prueba)
  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

  /** Resumen: conexión, último reporte, movimiento y conductor. */
  public get resumen(): {
    conexion: EstadoConexion;
    hace?: string;
    fecha?: string;
    movimiento?: { tono: TonoEstado; etiqueta: string };
    viaje?: string;
    conductor: string;
  } {
    // Conexión y último reporte con el más nuevo de cualquier tipo (como
    // tracker): un reporte de combustible también es el equipo comunicando.
    const fecha = this.reporteReciente?.fechaCreacion;
    const minutos = minutosDesde(fecha);
    const estado = this.trackerStatus?.currentState?.state;
    const viaje = this.trackerStatus?.currentTrip;
    return {
      conexion: estadoConexion(minutos, this.activo?.tracker?.frecReporte),
      hace: fecha ? haceCuanto(fecha) : undefined,
      fecha: fecha ? HelperService.getFechaYHora(fecha) : undefined,
      movimiento: estado
        ? (MOVIMIENTO[estado] ?? MOVIMIENTO['UNKNOWN'])
        : undefined,
      viaje: viaje?.startTime
        ? `en viaje desde hace ${haceCuanto(viaje.startTime)}`
        : undefined,
      conductor:
        this.chofer?.datosPersonales?.nombre ||
        this.chofer?.usuario ||
        'Sin conductor asignado',
    };
  }

  /**
   * Odómetro: el del CAN (el del tablero) si el vehículo lo reporta; si no, el
   * del equipo o el de Tripero, que son estimados por GPS.
   */
  public get odometro(): FilaDato {
    const can = this.itemsObd.find((i) => i.label === 'Odómetro del vehículo');
    if (can) return { etiqueta: 'Odómetro', valor: `${can.valor} km` };
    const km = this.odometroHistorialKm;
    return {
      etiqueta: 'Odómetro (estimado por GPS)',
      valor: km != null ? `${Math.round(km).toLocaleString('es-AR')} km` : null,
    };
  }

  public get filasVehiculo(): FilaDato[] {
    const v = this.activo?.vehiculo;
    const marcaModelo = [v?.marca, v?.modelo].filter(Boolean).join(' ');
    return [
      { etiqueta: 'Marca y modelo', valor: marcaModelo || null },
      { etiqueta: 'Año', valor: v?.anio },
      { etiqueta: 'Flota', valor: this.activo?.grupo?.nombre },
      this.odometro,
    ];
  }

  public get filasChofer(): FilaDato[] {
    const d = this.chofer?.datosPersonales;
    const filas: FilaDato[] = [
      { etiqueta: 'Nombre', valor: d?.nombre || this.chofer?.usuario },
      { etiqueta: 'DNI', valor: d?.dni, mono: true, copiable: true },
      { etiqueta: 'Teléfono', valor: d?.telefono, copiable: true },
      { etiqueta: 'Email', valor: d?.email, copiable: true },
    ];
    if (d?.sexo !== null && d?.sexo !== undefined) {
      filas.push({
        etiqueta: 'Sexo',
        valor: d.sexo ? 'Masculino' : 'Femenino',
      });
    }
    if (d?.fechaNacimiento) {
      filas.push({
        etiqueta: 'Fecha de nacimiento',
        valor: HelperService.getFecha(d.fechaNacimiento),
      });
    }
    if (d?.direccion) filas.push({ etiqueta: 'Dirección', valor: d.direccion });
    if (d?.pais) filas.push({ etiqueta: 'País', valor: d.pais });
    return filas;
  }

  /** Tracker y comunicación juntos (antes eran dos tarjetas). */
  public get filasTracker(): FilaDato[] {
    const t = this.activo?.tracker;
    if (!t) return [{ etiqueta: 'Tracker', valor: 'Sin tracker asignado' }];
    const marcaModelo = [t.modelo?.marca, t.modelo?.modelo]
      .filter(Boolean)
      .join(' ');
    const filas: FilaDato[] = [
      { etiqueta: 'Nombre', valor: t.nombre },
      { etiqueta: 'Marca y modelo', valor: marcaModelo || null },
      { etiqueta: 'ID único', valor: t.uniqueId, mono: true, copiable: true },
      {
        etiqueta: 'Frecuencia de reporte',
        valor: t.frecReporte ? `cada ${duracion(t.frecReporte)}` : null,
      },
    ];
    const datos = this.infoTableTracker?.data ?? {};
    const acciones = this.infoTableTracker?.fieldActions ?? {};
    for (const clave of ['Reglas de reenvío', 'Emergencia AVL']) {
      if (!(clave in datos)) continue;
      const a = acciones[clave];
      filas.push({
        etiqueta: clave,
        valor: datos[clave],
        accion: a
          ? {
              icono: a.icon,
              tooltip: a.tooltip ?? '',
              click: a.click,
              disabled: a.disabled,
            }
          : undefined,
      });
    }
    return filas;
  }

  /**
   * Dashcam asignada. Sin dashcam la plantilla muestra "Sin dashcam asignada"
   * en vez de estas filas. Todavía sin botón "Ver en vivo".
   */
  public get filasDashcam(): FilaDato[] {
    const d = this.dashcam;
    if (!d) return [];
    const marcaModelo = [d.modelo?.marca, d.modelo?.modelo]
      .filter(Boolean)
      .join(' ');
    const fecha = this.fechaReporteDashcam;
    const conexion = estadoConexion(minutosDesde(fecha), d.frecReporte);
    const sim = d.sim1?.numero || d.sim1?.iccid;
    return [
      { etiqueta: 'Nombre', valor: d.nombre || d.identificacion },
      { etiqueta: 'Marca y modelo', valor: marcaModelo || null },
      {
        etiqueta: 'Estado de conexión',
        valor: fecha
          ? `${conexion.etiqueta} · hace ${haceCuanto(fecha).toLowerCase()}`
          : conexion.etiqueta,
      },
      { etiqueta: 'SIM', valor: sim, mono: true, copiable: true },
    ];
  }

  /** Viaje en curso, sin estado ni odómetro (van al resumen y a Vehículo). */
  public get filasViaje(): FilaDato[] {
    const datos = this.infoTableViajeActual?.data ?? {};
    return Object.entries(datos)
      .filter(([k]) => k !== 'Estado' && k !== 'Odómetro')
      .map(([etiqueta, valor]) => ({ etiqueta, valor: valor as string }));
  }

  /** Combustible con el nivel y el consumo promedio adentro. */
  public get filasCombustible(): FilaDato[] {
    const datos = this.infoTableCombustible?.data ?? {};
    const filas: FilaDato[] = Object.entries(datos).map(
      ([etiqueta, valor]) => ({
        etiqueta,
        valor: valor as string,
      }),
    );
    const r = this.statCardRendimiento;
    if (r?.value) {
      // Sin datos, el motivo (p. ej. "Solo 0 km recorridos") dice más que
      // "Sin datos" a secas; con datos, el valor y su detalle.
      const sinDatos = r.value === 'Sin datos';
      filas.push({
        etiqueta: 'Consumo promedio (30 días)',
        valor: sinDatos
          ? r.subtitle || 'Sin datos'
          : [
              r.value,
              r.subtitle?.replace(/ · últimos 30 días$|^últimos 30 días$/, ''),
            ]
              .filter(Boolean)
              .join(' · '),
      });
    }
    return filas;
  }

  /** OBD sin el odómetro (va en Vehículo). */
  public get filasObd(): FilaDato[] {
    return this.itemsObd
      .filter((i) => i.label !== 'Odómetro del vehículo')
      .map((i) => ({
        etiqueta: i.label,
        valor: i.valor,
        mono: i.label === 'VIN',
        copiable: i.label === 'VIN',
      }));
  }

  /**
   * Alto del mapa en px: el de su lugar en la grilla (las dos primeras
   * tarjetas). map-card le resta su encabezado al alto que recibe: con "100%"
   * lo restaría dos veces. Que Mapbox se redimensione lo resuelve map-card.
   */
  public pantallaAncha = signal(window.innerWidth > 1100);
  public altoMapa = signal<number | null>(null);
  private observadorMapa?: ResizeObserver;

  @ViewChild('tarjetaMapa') set tarjetaMapa(
    ref: ElementRef<HTMLElement> | undefined,
  ) {
    this.observadorMapa?.disconnect();
    if (!ref) return;
    this.observadorMapa = new ResizeObserver(([entrada]) => {
      this.altoMapa.set(Math.round(entrada.contentRect.height) || null);
    });
    this.observadorMapa.observe(ref.nativeElement);
  }

  /**
   * Los gráficos van dentro de una app-dt-card (que pone el título): se les
   * saca el suyo. Memorizado: un objeto nuevo en cada detección de cambios
   * haría que el gráfico se redibuje.
   */
  private sinTituloCache = new WeakMap<ChartCardConfig, ChartCardConfig>();
  public sinTitulo(c: ChartCardConfig | null): ChartCardConfig | null {
    if (!c) return null;
    let r = this.sinTituloCache.get(c);
    if (!r) {
      r = { ...c, title: undefined, showShadow: false };
      this.sinTituloCache.set(c, r);
    }
    return r;
  }

  @HostListener('window:resize')
  public alRedimensionar(): void {
    this.pantallaAncha.set(window.innerWidth > 1100);
  }

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  // TARJETAS VISIBLES
  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

  public actualizarTarjetas(): void {
    const visibles = new Set<string>([
      'vehiculo',
      'mapa',
      'tracker',
      'dashcam',
      'viaje',
    ]);
    if (this.chofer) visibles.add('chofer');
    if (this.filasCombustible.length) visibles.add('combustible');
    if (this.activo?.vehiculo?.recorrido) visibles.add('recorrido');
    if (this.statCardTemperatura || this.chartCardConfigTemperatura) {
      visibles.add('temperatura');
    }
    if (this.chartCardConfigNivelCombustible) visibles.add('nivelCombChart');
    if (this.chartCardConfigConsumoCombustible) visibles.add('consumoChart');
    // Cualquier señal de combustible habilita los informes: un vehículo con
    // histórico pero sensor caído no debería perder la tarjeta
    if (
      this.statCardNivelCombustible ||
      this.chartCardConfigNivelCombustible ||
      this.chartCardConfigConsumoCombustible
    ) {
      visibles.add('informeCombustible');
    }
    if (this.filasObd.length) visibles.add('obd');
    if (this.activo?.imagenes?.length) visibles.add('documentacion');
    this.tarjetas = this.TARJETAS.filter((t) => visibles.has(t.id));
  }
}
